/*
 * Custom currency converter — replaces the Nova Currency Converter app.
 *
 * No visible switcher: the displayed currency is derived automatically from
 * the shopper's selected country/market (our own header country selector,
 * which already causes a page reload via Shopify's localization form).
 *
 *   Serbia (RS)                -> RSD (shop's own currency, left untouched)
 *   Russia (RU)                -> RUB
 *   United Kingdom (GB)        -> GBP
 *   The Americas (US/CA/MX/…)  -> USD
 *   Everywhere else            -> EUR
 *
 * Visual conversion only: rewrites the already-rendered `.money` price spans
 * that Shopify's money_format wraps every price in (theme-wide, no template
 * changes needed). Cart/checkout still charge in RSD — this only changes
 * what is displayed.
 */
(function () {
  if (window.__currencyConverterInit) return;
  window.__currencyConverterInit = true;

  var STORAGE_RATES_KEY = 'currency_converter_rates_v1';
  var RATES_TTL_MS = 24 * 60 * 60 * 1000; // 24h
  var BASE_CURRENCY = (window.theme && window.theme.currencyCode) || 'RSD';
  var SUPPORTED = ['RSD', 'EUR', 'USD', 'RUB', 'GBP'];
  var API_URL = 'https://open.er-api.com/v6/latest/' + BASE_CURRENCY;

  var SYMBOLS = { RSD: 'RSD', EUR: '€', USD: '$', RUB: '₽', GBP: '£' };
  var DECIMALS = { RSD: 0, EUR: 2, USD: 2, RUB: 0, GBP: 2 };

  // ISO 3166-1 alpha-2 codes for North, Central, South America and the Caribbean.
  var AMERICAS = [
    'US', 'CA', 'MX', 'BZ', 'CR', 'SV', 'GT', 'HN', 'NI', 'PA',
    'AG', 'BS', 'BB', 'CU', 'DM', 'DO', 'GD', 'HT', 'JM', 'KN', 'LC', 'VC',
    'TT', 'TC', 'VG', 'VI', 'AI', 'AW', 'BQ', 'BM', 'KY', 'CW', 'GP', 'MQ', 'MS', 'PR', 'SX', 'BL', 'MF',
    'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'FK', 'GF', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE',
  ];

  function currencyForCountry(iso) {
    if (!iso) return 'EUR';
    iso = iso.toUpperCase();
    if (iso === 'RS') return 'RSD';
    if (iso === 'RU') return 'RUB';
    if (iso === 'GB') return 'GBP';
    if (AMERICAS.indexOf(iso) > -1) return 'USD';
    return 'EUR';
  }

  var state = {
    rates: null, // relative to BASE_CURRENCY, e.g. { EUR: 0.0085, ... }
    currency: currencyForCountry(window.theme && window.theme.countryCode),
  };

  // ---- rate cache ----

  function readCache() {
    try {
      var raw = localStorage.getItem(STORAGE_RATES_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.rates || !data.ts) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(rates) {
    try {
      localStorage.setItem(STORAGE_RATES_KEY, JSON.stringify({ rates: rates, ts: Date.now() }));
    } catch (e) {}
  }

  function fetchRates() {
    return fetch(API_URL, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('rate fetch failed');
        return res.json();
      })
      .then(function (json) {
        if (!json || !json.rates) throw new Error('bad rate payload');
        var rates = {};
        SUPPORTED.forEach(function (code) {
          if (code === BASE_CURRENCY) {
            rates[code] = 1;
          } else if (json.rates[code]) {
            rates[code] = json.rates[code];
          }
        });
        writeCache(rates);
        return rates;
      });
  }

  function ensureRates() {
    var cached = readCache();
    if (cached && Date.now() - cached.ts <= RATES_TTL_MS) {
      state.rates = cached.rates;
      return Promise.resolve(state.rates);
    }
    return fetchRates()
      .then(function (rates) {
        state.rates = rates;
        return rates;
      })
      .catch(function () {
        // Network/API failure: fall back to a stale cache rather than nothing.
        if (cached) {
          state.rates = cached.rates;
          return state.rates;
        }
        state.rates = null;
        return null;
      });
  }

  function convert(value, toCode) {
    if (!state.rates || toCode === BASE_CURRENCY) return value;
    var rate = state.rates[toCode];
    if (!rate) return null;
    return value * rate;
  }

  var LOCALES = { RSD: 'sr-RS', EUR: 'en-US', USD: 'en-US', RUB: 'en-US', GBP: 'en-US' };

  function formatAmount(value, code) {
    var decimals = DECIMALS[code] != null ? DECIMALS[code] : 2;
    var formatted;
    try {
      formatted = new Intl.NumberFormat(LOCALES[code] || 'en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch (e) {
      formatted = value.toFixed(decimals);
    }
    var symbol = SYMBOLS[code] || code;
    if (code === 'USD' || code === 'GBP') return symbol + formatted;
    return formatted + ' ' + symbol; // RSD, EUR, RUB after the amount
  }

  function parseAmount(text) {
    var m = text.match(/\d[\d.,]*\d|\d+/);
    if (!m) return null;
    var raw = m[0];
    var lastSepMatch = raw.match(/([.,])(\d+)$/);
    var decimalSep = lastSepMatch && lastSepMatch[2].length <= 2 ? lastSepMatch[1] : null;
    var normalized;
    if (decimalSep) {
      var thousandSep = decimalSep === '.' ? ',' : '.';
      normalized = raw.split(thousandSep).join('').replace(decimalSep, '.');
    } else {
      normalized = raw.replace(/[.,]/g, '');
    }
    var value = parseFloat(normalized);
    return isNaN(value) ? null : value;
  }

  // ---- DOM: Shopify's money_format wraps every rendered price in a
  // `<span class="money">` element theme-wide, so that's our single,
  // reliable conversion target (no template edits needed anywhere else). ----

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1 };

  function shouldSkip(el) {
    while (el) {
      if (SKIP_TAGS[el.tagName]) return true;
      el = el.parentElement;
    }
    return false;
  }

  function convertMoneyElements(root) {
    if (!state.rates) return;
    var spans = root.querySelectorAll ? root.querySelectorAll('.money') : [];
    Array.prototype.forEach.call(spans, function (span) {
      if (shouldSkip(span)) return;

      var original = span.getAttribute('data-currency-original');
      var sourceText = original != null ? original : span.textContent;
      if (original == null) span.setAttribute('data-currency-original', span.textContent);

      var amount = parseAmount(sourceText);
      if (amount == null) return;
      var converted = convert(amount, state.currency);
      if (converted == null) return;
      span.textContent = formatAmount(converted, state.currency);
    });
  }

  function applyCurrency() {
    convertMoneyElements(document.body);
  }

  function boot() {
    applyCurrency(); // no-op until rates load, but wires the observer below immediately

    ensureRates().then(function () {
      applyCurrency();
    });

    var pending = null;
    var observer = new MutationObserver(function (mutations) {
      var hasAdded = mutations.some(function (m) {
        return m.addedNodes && m.addedNodes.length;
      });
      if (!hasAdded) return;
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () {
        if (state.rates) applyCurrency();
      }, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();